/* global braintree */
import { fetcher } from './fetcher';
import { checkPaymentStatusAndRedirect } from './payments';
import { getValidPromoCode } from './payments/promo-validity-handler';
import { buildPaymentInitializationPayload, getPaymentInitialization } from './payments/payment-initialization';
import { getDiscountFlow } from './payments/discount-flow';
import store from './store';

let submitted = false;

window.braintreeDropInV1Loaded = false;

const loadBraintreeDropInV1 = () => {
  const provider = 'braintree-drop-in-v1';

  // don't load multiple times in any case
  if (window.braintreeDropInV1Loaded) {
    return;
  }

  submitted = false;

  // wait until all libs are loaded
  if (
    typeof braintree === 'undefined' ||
    typeof braintree.client === 'undefined' ||
    typeof braintree.dropin === 'undefined'
  ) {
    return;
  }

  window.braintreeDropInV1Loaded = true;

  const form = getFormNode(provider);
  const formButton = getFormButton(provider);
  const settings = getSettings();
  const geoip = buildGeoip();
  const dropIn = setUpDropIn(provider, settings);

  const init = async () => {
    let dropinInstance;
    let threeDSecureInstance;

    try {
      dropinInstance = await braintree.dropin.create(dropIn.settings);
      threeDSecureInstance = await braintree.threeDSecure.create({
        authorization: settings.auth,
        version: 2,
      });
    } catch (err) {
      return Promise.reject('Authentication is invalid.');
    }

    payments.formLoader.hide();

    form.element.addEventListener('submit', e => {
      e.preventDefault();

      if (submitted) {
        return;
      } else {
        submitted = true;
      }

      const errorNode = document.querySelector(`[data-error-field="${provider}"]`);
      errorNode.classList.add('hidden');

      const priceDOM = document.querySelector('#price');
      const price = priceDOM.value ? priceDOM.value : backEndVars.paymentTypes['braintree'].price;

      dropinInstance.requestPaymentMethod((err, payload) => {
        if (err) {
          console.log('tokenization error:');
          console.log(err);
          dropinInstance.clearSelectedPaymentMethod();
          submitted = false;

          return;
        }

        const name = payload.details.cardholderName.trim().split(' ');
        const firstName = name[0];
        const lastName = name.length > 1 ? name[name.length - 1] : '';

        payments.formLoader.show();
        payments.paymentFormCloseButton.hide();

        getPaymentInitialization(buildPaymentInitializationPayload({ provider: 'braintree', nonce: payload.nonce }))
          .then(async response => {
            threeDSecureInstance.verifyCard(
              buildVerifyCardOptions({
                firstName,
                lastName,
                nonce: response.nonce,
                bin: response.details.bin,
                price,
                email: store.getAnswers()['saved-email'],
                geoip,
              }),
              (verifyError, verifyResponse) => {
                if (verifyError || !verifyResponse.liabilityShifted || !verifyResponse.liabilityShiftPossible) {
                  renderVerificationError();

                  payments.formError.display(provider, 'Something went wrong. Please try again');
                  payments.paymentFormCloseButton.show();
                  payments.formLoader.hide();

                  submitted = false;

                  return;
                }

                payments.paymentFormCloseButton.hide();
                dropIn.hide();
                payments.formLoader.show();

                const payload = buildExecutePaymentPayload(verifyResponse);

                dropIn.executePayment(payload, dropinInstance);

                submitted = false;
              },
            );
          })
          .catch(({ error }) => {
            payments.formError.display(provider, 'Something went wrong. Please try again');
            payments.paymentFormCloseButton.show();
            payments.formLoader.hide();
            submitted = false;
          });
      });
    });

    renderPaymentSelector(submitted, provider);
  };

  init().catch(message => {
    payments.formError.display(provider, message);
    formButton.disable();
    payments.formLoader.hide();
  });

  return true;
};

export const buildExecutePaymentPayload = response => {
  return {
    ...response,
    uuid: window.getUuid(),
    customerUuid: window.getCustomerUuid(),
    email: store.getAnswers()['saved-email'] || null,
    paymentMethod: 'braintree',
    price: document.querySelector('#price').value,
    plan: document.querySelector('#braintree-plan').value,
    bundleId: document.querySelector('#bundle-id')?.value,
    isSubscriptionBoxes: backEndVars.isExperimentsEnabled,
    pageSlug: backEndVars.pageSlug || '',
    purchasableCategory: backEndVars.purchasableCategory || '',
    purchasableType: backEndVars.purchasableType || '',
    checkoutOffer: backEndVars.checkoutOffer,
    type: backEndVars.type || '',
    promoCode: getValidPromoCode(),
    discountFlow: getDiscountFlow(),
  };
};

export const setUpDropIn = (provider, settings) => {
  return {
    container: document.querySelector('#dropin-container'),
    hide() {
      this.container.classList.add('hidden');
    },
    show() {
      this.container.classList.remove('hidden');
    },
    showCardForm(dropinInstance) {
      this.show();
      dropinInstance.clearSelectedPaymentMethod();
    },
    executePayment(payload, dropinInstance) {
      fetcher
        .post(backEndVars.braintreeDropInFormUrl, payload, { 'X-Requested-With': 'XMLHttpRequest' })
        .then(response => {
          executePaymentOnSuccessCb({ response, provider, payments, window, payload, dropinInstance });
        })
        .catch(({ status, error }) => {
          executePaymentOnErrorCb({ status, error, provider, payments, dropinInstance });
        });
    },
    settings: {
      authorization: settings.auth,
      container: settings.container,
      dataCollector: {
        kount: true, // enable kount anti-theft protection
      },
      card: {
        cardholderName: {
          required: true,
        },
      },
    },
  };
};

export const showCardform = dropinInstance => {
  document.querySelector('#dropin-container').classList.remove('hidden');
  dropinInstance.clearSelectedPaymentMethod();
};

export const executePaymentOnSuccessCb = ({ response, provider, payments, window, payload, dropinInstance }) => {
  if (!response || !response.ok) {
    payments.formError.display(provider, 'Something went wrong. Please try again');
    payments.paymentFormCloseButton.show();
    return;
  }

  const data = {};
  data.pageSlug = payload.pageSlug;
  data.checkoutOffer = backEndVars.checkoutOffer;

  submitted = payments.checkPaymentStatusAndRedirect(
    provider,
    new Url().build('products', payload.uuid, 'is-paid'),
    data,
  );

  showCardform(dropinInstance);
};

export const executePaymentOnErrorCb = ({ status, error, provider, payments, dropinInstance }) => {
  if (status === 455) {
    new Url().redirectTo('meal-plan-error');
  } else if (status === 404) {
    new Url().redirectTo('/home');
  } else {
    payments.formError.display(provider, error);
    payments.formLoader.hide();
    payments.paymentFormCloseButton.show();
  }

  showCardform(dropinInstance);
};

export const renderPaymentSelector = (submitted, provider) => {
  document.querySelector('#dropin-container .braintree-toggle').addEventListener('click', () => {
    if (!submitted) {
      document.querySelector(".m-card-details__controls input[type='submit']").style.display = 'block';
      document.querySelector('.braintree-method--active').style.display = 'none';
      payments.formError.clear(provider);
    }
  });
};

export const renderVerificationError = () => {
  document.querySelector('.braintree-method__check use').setAttribute('xlink:href', '#iconX');
  document.querySelector('.braintree-method--active').classList.add('braintree-error__message');
  document.querySelector('.braintree-method__check').classList.add('braintree-error__icon');
  document.querySelector('.m-card-details__controls input[type="submit"]').style.display = 'none';
};

export const getFormNode = provider => {
  return {
    element: document.querySelector(`#${provider}-card-details`),
    isBraintree() {
      return backEndVars.paymentTypes && backEndVars.paymentTypes.braintree;
    },
    paymentMethod() {
      return this.isBraintree() ? 'braintree' : null;
    },
  };
};

export const getFormButton = provider => {
  return {
    element: document.querySelector(`#${provider}-card-details input[type="submit"]`),
    enable() {
      this.element.disabled = false;
      this.element.classList.remove('disabled');
    },
    disable() {
      this.element.disabled = true;
      this.element.classList.add('disabled');
    },
  };
};

export const getSettings = () => {
  return {
    auth: document.querySelector('#braintree-drop-in-token').textContent,
    container: '#dropin-container',
  };
};

export const buildGeoip = () => {
  return {
    city: document.querySelector('#braintree-drop-in-geoip-city').textContent,
    postalCode: document.querySelector('#braintree-drop-in-geoip-postal-code').textContent,
    region: document.querySelector('#braintree-drop-in-geoip-region').textContent,
    countryName: document.querySelector('#braintree-drop-in-geoip-country-name').textContent,
    countryCode: document.querySelector('#braintree-drop-in-geoip-country-code').textContent,
    clientIp: document.querySelector('#braintree-drop-in-geoip-client-ip').textContent,
  };
};

export const buildVerifyCardOptions = options => {
  return {
    amount: options.price,
    nonce: options.nonce,
    bin: options.bin,
    email: options.email,
    collectDeviceData: true,
    billingAddress: {
      givenName: options.firstName,
      surname: options.lastName,
      locality: options.geoip.city,
      region: options.geoip.region,
      postalCode: options.geoip.postalCode,
      countryCodeAlpha2: options.geoip.countryCode,
    },
    additionalInformation: {
      acsWindowSize: '05',
      ipAddress: options.geoip.clientIp,
      shippingGivenName: options.firstName,
      shippingSurname: options.lastName,
      shippingAddress: {
        locality: options.geoip.city,
        region: options.geoip.region,
        postalCode: options.geoip.postalCode,
        countryCodeAlpha2: options.geoip.countryCode,
      },
    },
    exemptionRequested: true,
    onLookupComplete: (data, next) => {
      // Use look-up data here
      next();
    },
  };
};
export const startPaymentProcessBraintree = data => {
  getPaymentInitialization(buildPaymentInitializationPayload({ provider: 'braintree', uuid: data.uuid })).then(() =>
    handlePaymentProcessBraintree(data),
  );
};

export const handlePaymentProcessBraintree = data => {
  const payload = {
    uuid: data.uuid,
    customerUuid: data.customerUuid,
    email: store.getAnswers()['saved-email'] || null,
    paymentMethod: 'braintree',
    price: document.querySelector('#price').value,
    plan: document.querySelector('#braintree-plan').value,
    bundleId: document.querySelector('#bundle-id')?.value,
    isSubscriptionBoxes: backEndVars.isExperimentsEnabled,
    pageSlug: backEndVars.pageSlug || '',
    purchasableCategory: backEndVars.purchasableCategory || '',
    purchasableType: backEndVars.purchasableType || '',
    checkoutOffer: backEndVars.checkoutOffer || '',
    type: backEndVars.type || '',
  };

  fetcher
    .post(backEndVars.braintreeDropInFormUrl, payload, {
      Accept: 'application/json',
    })
    .then(async response => {
      response.checkoutOffer = backEndVars.checkoutOffer;

      checkPaymentStatusAndRedirect('braintree', new Url().build('products', response.uuid, 'is-paid'), response);
    })
    .catch(error => {
      console.error(error);
    });
};

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.braintree-drop-in-v1')) {
    const interval = setInterval(() => {
      if (loadBraintreeDropInV1()) {
        clearInterval(interval);
      }
    }, 1000);
  }
});
